import { cn } from "@/lib/utils";

interface HotspotPinProps {
  index: number;
  /** Names the pin for assistive tech, which a bare number cannot. */
  title: string;
  x: number;
  y: number;
  isActive: boolean;
  /** Id of the card this pin opens, for `aria-controls`. */
  cardId: string;
  /** Set once the graphic is on screen, so the three runs are not spent early. */
  pulse: boolean;
  onActivate: (e: React.SyntheticEvent, index: number) => void;
}

const HotspotPin = ({
  index,
  title,
  x,
  y,
  isActive,
  cardId,
  pulse,
  onActivate,
}: HotspotPinProps) => (
  <div
    className={`absolute -translate-x-1/2 -translate-y-1/2 ${
      isActive ? "z-20" : "z-10"
    }`}
    style={{ left: `${x * 100}%`, top: `${y * 100}%` }}
  >
    <button
      // The visible number is the label for sighted learners; the title is what
      // makes the pin worth activating, so it belongs in the accessible name.
      aria-label={`Hotspot ${index + 1}: ${title}`}
      aria-expanded={isActive}
      aria-controls={isActive ? cardId : undefined}
      onClick={(e) => onActivate(e, index)}
      onMouseOver={(e) => onActivate(e, index)}
      // Keyboard learners reach the pin by tabbing; opening on focus gives them
      // the same reading flow the pointer gets on hover.
      onFocus={(e) => onActivate(e, index)}
      className={cn(
        "relative flex h-8 w-8 items-center justify-center rounded-full",
        "border-2 border-white text-xs font-bold tabular-nums text-white",
        // A thin dark edge outside the white ring: photographs are bright in
        // one corner and dark in the next, and the white ring alone disappears
        // against the light ones.
        "shadow-md ring-1 ring-black/15",
        // Grows the touch target to ~44px without enlarging the pin.
        "before:absolute before:-inset-1.5 before:content-['']",
        "cursor-pointer transition-[transform,background-color] duration-150",
        "focus-visible:ring-2 focus-visible:ring-secondary-bright focus-visible:ring-offset-2 focus-visible:outline-none",
        // Violet is the brand's interaction colour: it marks the pin whose card
        // is open, and only while it is open — a pin the learner has already
        // read looks like any other.
        isActive
          ? "scale-110 bg-secondary-deep ring-1 ring-primary-bright/70"
          : "bg-primary scale-110 ring-1 hover:bg-primary"
      )}
    >
      {/*
        Three pulses are an invitation to click; an endless one is a distraction
        for the rest of the time the learner spends on the page. The ring is
        mounted with the animation rather than kept around unanimated, so the
        three runs start the moment the graphic comes into view. White rather
        than navy: the pulse has to read on dark imagery too.
      */}
      {pulse && (
        <span
          data-testid="hotspot-pulse"
          className="absolute h-8 w-8 rounded-full bg-white/60 animate-ping-3"
        />
      )}
      {index + 1}
    </button>
  </div>
);

export default HotspotPin;
